/* eslint-disable complexity */
import React, { useMemo, useState } from "react";
import moment from "moment";
import SignOffAccordionItem from "protocol-setup/components/SignOffAccordionItem";
import { getNextStatus, getStatusPosition, useSetupPermission } from "protocol-setup/helpers";
import ProtectionElements from "protocol-setup/helpers/ProtectionElement";
import { useStatusHistory } from "protocol-setup/redux/useLovs";
import { FasPencilAlt, FasTimes } from "assets/icons";
import OpenRaveSubmitForm from "./OpenRaveSubmitForm";
import GroupTestingForm from "./GroupTestingForm";
import CTSUTestingForm from "./CTSUTestingForm";
import PendingSignOffForm from "./PendingSignOffForm";
import CompletedForm from "./CompletedForm";
import UATSetupForm from "./UATSetupFrom";
import { getLatestSignoffInfo } from "./PendingSignOffForm/pendingSignOffUtils";


const updateDateFormat = (inputDate) => {
  return inputDate ? moment(inputDate, "MM/DD/YYYY hh:mm:ss:SS A").format("DD-MMM-YYYY hh:mm:ss A") : '';
}
const getInfofromDtoList = (obj) => {
  return {
    submitterName: obj?.SUBMITTED_BY_NAME || '',
    submittedDate: updateDateFormat(obj?.SUBMITTED_DT)
  };
}

const getOpenRaveSubmitterInfo = (protocolSetupData) => {
  const primaryOpenAdmin = protocolSetupData?.protSetupContacts?.find((c) => c.contactType === "PRIMARY_OPEN_ADMIN")?.perId;
  if (primaryOpenAdmin) {
    return {
      submitterName: protocolSetupData?.requestSubmittedBy || '',
      submittedDate: protocolSetupData?.requestSubmittedDt || ''
    }
  }
  return {
    submitterName: '',
    submittedDate: ''
  };
}

const getSubmitterInfo = (protocolSetupData, nextStatus) => {
  let data = {}

  switch (nextStatus) {
    case "UAT_SETUP":
      data = getOpenRaveSubmitterInfo(protocolSetupData);
      break;

    case "GROUP_TESTING":
      data = {
        submitterName: protocolSetupData?.uatSetup?.submittedBy || '',
        submittedDate: protocolSetupData?.uatSetup?.submittedDt || ''
      }
      break;

    case "CTSU_TESTING":
      try {
        data = getInfofromDtoList(JSON.parse(protocolSetupData?.groupTestDtoList[0].userTypeCommentsJson)[0])
      } catch (e) {
        data = {}
      }
      break;

    case "PENDING_SIGNOFF":
      try {
        data = getInfofromDtoList(JSON.parse(protocolSetupData?.ctsuTestDtoList[0].userTypeCommentsJson)[0])
      } catch (e) {
        data = {}
      }
      break;

    case "COMPLETED":
      data = getLatestSignoffInfo(protocolSetupData?.signoffPending || {});
      break;

    case "OPTIONAL_TRACKING":
      data = {
        submitterName: protocolSetupData?.signoffPending?.setupVerifiedProdBy || '',
        submittedDate: protocolSetupData?.signoffPending?.setupCompletedProdDate || ''
      }
      break;

    default:
      {
        const signOffInfo = protocolSetupData?.signOffInfo || [];
        const signOffEntry = signOffInfo.find(entry => entry.status === nextStatus);
        data = {
          submitterName: signOffEntry?.signedOffByName || '',
          submittedDate: signOffEntry?.signedOffDate || ''
        };
      }
      break;
  }
  return data;
}


const FORM_COMPONENTS = {
  SUBMITTED: OpenRaveSubmitForm,
  UAT_SETUP: UATSetupForm,
  GROUP_TESTING: GroupTestingForm,
  CTSU_TESTING: CTSUTestingForm,
  PENDING_SIGNOFF: PendingSignOffForm,
  COMPLETED: CompletedForm
};


const PANEL_CONFIG = [
  { title: "Submitted", status: "SUBMITTED" },
  { title: "UAT Setup", status: "UAT_SETUP" },
  { title: "Group Testing", status: "GROUP_TESTING" },
  { title: "CTSU Testing", status: "CTSU_TESTING" },
  { title: "Pending Sign Off", status: "PENDING_SIGNOFF" },
  { title: "Completed", status: "COMPLETED" }
];

const permissionMap = {
  SUBMITTED: ProtectionElements.OPEN_SIGNOFF_SUBMITTED,
  UAT_SETUP: ProtectionElements.OPEN_SIGNOFF_UATSETUP,
  GROUP_TESTING: ProtectionElements.OPEN_SIGNOFF_GROUPTESTING,
  CTSU_TESTING: ProtectionElements.OPEN_SIGNOFF_CTSUTESTING,
  PENDING_SIGNOFF: [ProtectionElements.OPEN_SIGNOFF_PENDINGSIGNOFF_GROUP, ProtectionElements.OPEN_SIGNOFF_PENDINGSIGNOFF_IROC, ProtectionElements.OPEN_SIGNOFF_PENDINGSIGNOFF_CTSU],
  COMPLETED: ProtectionElements.OPEN_SIGNOFF_COMPLETE
};

function SignOffForms({ protocolSetupData, fetchPsData }) {
  const { requestStatus: currentStatus, formPartType, signoffPending, requestNumber } = protocolSetupData;
  const { setupCompletedProd, formsActivatedProd, optionalTrackingComments } = signoffPending || {};

  const [isPendingSignOffEditMode, setIsPendingSignOffEditMode] = useState(false);
  const hasGroupEditPermission = useSetupPermission(ProtectionElements.OPEN_SIGNOFF_PENDINGSIGNOFF_GROUP);
  const isCompletedRequest = currentStatus === 'COMPLETED';

  const { statusHistory } = useStatusHistory(requestNumber);


  const requestStatus = useMemo(() => {
    let status = currentStatus;

    if (currentStatus === "RETIRED" || currentStatus === "CANCELED") {
      const historyEntry = (statusHistory || []).find(
        (entry) => entry.toState === currentStatus
      );

      if (historyEntry && historyEntry.fromState) {
        const fromStatus = historyEntry.fromState;
        const fromStatusIndex = PANEL_CONFIG.findIndex(
          (panel) => panel.status === fromStatus
        );
        if (fromStatusIndex > 0 && fromStatusIndex < PANEL_CONFIG.length-2) {
          status = PANEL_CONFIG[fromStatusIndex - 1].status;
        } else {
          status = fromStatus;
        }
      }
    }

    if (
      formPartType === "OPEN_RAVE" &&
      status === "COMPLETED" &&
      (setupCompletedProd || formsActivatedProd || optionalTrackingComments)
    ) {
      status = "OPTIONAL_TRACKING";
    }

    return status;
  }, [
    currentStatus,
    statusHistory,
    formPartType,
    setupCompletedProd,
    formsActivatedProd,
    optionalTrackingComments
  ]);

  const currentDateTime = useMemo(() => new Date().toLocaleString(), []);
  const panels = useMemo(() =>
    PANEL_CONFIG.map(({ title, status }) => {
      const FormComponent = FORM_COMPONENTS[status];
      const statusIndex = getStatusPosition(requestStatus, status, formPartType);
      return {
        title,
        status,
        statusIndex,
        FormComponent
      };
    }),
    [requestStatus, formPartType]
  );

  return (
    <>
      {panels.map(({ title, status, statusIndex, FormComponent }) => {
        const isReadOnly = statusIndex < 0 || currentStatus === 'RETIRED' || currentStatus === 'CANCELED';
        const isPendingSignOff = status === 'PENDING_SIGNOFF';
        const canEditPendingSignOff =
          isPendingSignOff &&
          isReadOnly &&
          isCompletedRequest &&
          hasGroupEditPermission;

        let pendingSignOffHeaderAction = null;

        if (isPendingSignOff && canEditPendingSignOff) {
          const isEditing = isPendingSignOffEditMode;
          const onToggleEditMode = () => setIsPendingSignOffEditMode(!isEditing);
          const onKeyDown = (e) => {
            if (e.key === "Enter") {
              onToggleEditMode();
            }
          };

          pendingSignOffHeaderAction = (
            <span
              className={`cursor-pointer fn-btn ${isEditing ? "close" : "edit"}`}
              onClick={onToggleEditMode}
              role="button"
              tabIndex={0}
              onKeyDown={onKeyDown}
            >
              {isEditing ? <FasTimes /> : <FasPencilAlt />}
            </span>
          );
        }

        return (
          <SignOffAccordionItem
            key={status}
            title={title}
            status={status}
            requestStatusIndex={statusIndex}
            currentDateTime={currentDateTime}
            requestStatus={requestStatus}
            data={getSubmitterInfo(protocolSetupData, getNextStatus(status, formPartType))}
            open={status === requestStatus}
            disable={statusIndex > 0}
            currentStatus={currentStatus}
            protectionElement={permissionMap[status]}
            headerAction={pendingSignOffHeaderAction}
            isEditMode={isPendingSignOff ? isPendingSignOffEditMode : false}
          >
            <FormComponent
              key={status}
              protocolSetupData={protocolSetupData}
              fetchPsData={fetchPsData}
              readOnly={isReadOnly}
              {...(isPendingSignOff && {
                isEditMode: isPendingSignOffEditMode,
                onToggleEditMode: setIsPendingSignOffEditMode
              })}
            />
          </SignOffAccordionItem>
        );
      })}
    </>
  );
}

export default SignOffForms;
